import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AnalyticsContext } from '@core/services/core/analytics/analytics-context';
import { ContextService } from '@core/services/core/context.service';
import { DeeplinkService } from '@core/services/core/deeplink.service';
import { TecuidamosService } from '@core/services/core/tecuidamos.service';
import { FamiliesHelperService } from '@core/services/helpers/families-helper.service';
import { ForgotHelperService } from '@core/services/helpers/forgot-helper.service';
import { GuidedLoginHelperService } from '@core/services/helpers/guided-login-helper.service';
import { ForgotPasswordService } from '@core/services/http-services/forgot-password.service';
import {
  BusinessLoginError,
  BusinessLoginErrorCodes, BusinessLoginUserData
} from '@models/business';
import { GuidedLoginParams, GuidedLoginServiceId } from '@models/guided-login';
import { PageAnalytics } from '@models/page-analytics';
import { ValidateEmailComponent } from '@modules/businesses/components/validate-email/validate-email.component';
import { LoginRouting, ResultLoginOptions } from '@modules/login/models';
import { RegisterHelperService } from '@modules/register/services/register-helper.service';
import { Observable } from 'rxjs';
import { ForgotPasswordState } from 'src/app/modules/login/pages/forget-password-step1/forget-password-step1.page';
import { ForgetOperation } from '../../models/operation-register';
import { ForgotPasswordOrigin, UserLoginStatus } from '../../models/user';
import { AuthService } from '../core/auth/auth.service';
import { HttpStatusCode } from '../core/base.service';
import { MMobileHelperService } from '../core/mmobile-helper.service';
import { NavigationHelper } from '../core/navigation-helper.service';
import { OperationRegisterService } from '../core/operation-register.service';
import { UiHelperService } from '../core/ui-helper.service';


@Injectable({
  providedIn: 'root'
})
export class LoginHelper {
  static readonly MAX_LOGIN_ATTEMPTS = 3;
  private incorrectLoginAttempts = new Map<string, number>();
  private guidedLoginParams: GuidedLoginParams;
  private optionsResult: Partial<ResultLoginOptions>;

  constructor(
    private mmobileHelper: MMobileHelperService,
    private ui: UiHelperService,
    private auth: AuthService,
    private nav: NavigationHelper,
    private or: OperationRegisterService,
    private ctx: ContextService,
    private registerHelperService: RegisterHelperService,
    private sanitizer: DomSanitizer,
    private familiesHelper: FamiliesHelperService,
    private teCuidamos: TecuidamosService,
    private deeplinkService: DeeplinkService,
    private guidedLoginHelper: GuidedLoginHelperService,
    private forgotHelper: ForgotHelperService,
    private forgotPasswordService: ForgotPasswordService,
    private analyticsContext: AnalyticsContext
  ) {}

  set options(opts: Partial<ResultLoginOptions>) {
    this.optionsResult = opts;
  }

  get options(): Partial<ResultLoginOptions> {
    return this.optionsResult;
  }

  getIncorrectAttempts(documentNumber: string) {
    return this.incorrectLoginAttempts.get(documentNumber) || 0;
  }

  checkAttemptsExceeded(documentNumber: string) {
    return (
      this.incorrectLoginAttempts.get(documentNumber) >=
      LoginHelper.MAX_LOGIN_ATTEMPTS
    );
  }

  resetIncorrectAttempts(documentNumber: string) {
    this.incorrectLoginAttempts.set(documentNumber, 0);
  }

  getLoginUrls() {
    return {
      urlUserBlock:
        this.mmobileHelper.getCustomConfigData('URLS', 'USER_BLOCKED') || '',
      aciCompaniesUrl:
        this.mmobileHelper.getCustomConfigData('URLS', 'ACICOMPANIES') || '',
      aciParticularsUrl:
        this.mmobileHelper.getCustomConfigData('URLS', 'ACIPARTICULARS') || '',
      forgotPasswordUrl:
        this.mmobileHelper.getAppOrPwa('URLS', 'FORGOT_PASSWORD') || '',
      businessLoginVideo:
        this.sanitizer.bypassSecurityTrustResourceUrl(this.mmobileHelper.getCustomConfigData('URLS', 'BUSINESS_LOGIN_VIDEO')) || '',
      businessAcieVideo:
        this.sanitizer.bypassSecurityTrustResourceUrl(this.mmobileHelper.getCustomConfigData('URLS', 'BUSINESS_ACIE_VIDEO')) || ''
    };
  }

  controlRGPDUE() {
    const { user } = this.auth.getState();
    const orStructure = {
      nif: user.documentNumber,
      nombre: user.name,
      apellido1: user.surname1,
      apellido2: user.surname2
    };
    const orProduct = 'ACI - Login';
    this.nav.toNew('operations/rgpdue-policy', {
      state: {
        type: 'login',
        isLogin: true,
        orStructure,
        orProduct,
        isPrivateArea: true
      }
    });
  }

  showModalIncorrectAttempts({
    incorrectAttempts,
    title,
    description,
    analytics,
    onSuccess
  }: {
    incorrectAttempts: number;
    title: string;
    description?: string;
    analytics?: PageAnalytics,
    onSuccess?: () => void
  }) {
    this.ui.presentOneButtonAlert({
      image: 'assets/images/result/error.svg',
      title,
      description,
      subdescription:
        incorrectAttempts < 2
          ? 'LOGIN.ERROR.ATTEMPT_1'
          : 'LOGIN.ERROR.ATTEMPT_2',
      analytics,
      onSuccess
    });
  }

  showModalAttemptsExceeded({
    title,
    description,
    analytics,
    onSuccess
  }: {
    title: string;
    description?: string;
    analytics?: PageAnalytics,
    onSuccess?: () => void
  }) {
    this.ui.presentOneButtonAlert({
      title,
      description,
      analytics,
      onSuccess,
      image: 'assets/images/result/error.svg',
      cssClass: !this.ctx.isApp ? ['business-login-alert'] : [],
    });
  }

  onErrorLogin(
    {
      remoteExtradata,
      remoteHttpStatus,
      remoteErrorCode
    }: { remoteExtradata?; remoteHttpStatus?, remoteErrorCode?},
    { documentNumber, password },
    showMatErrorsCallback: () => any,
    notShowMatErrorsCallback?: () => any
  ) {
    const { status: statusRaw, email } = remoteExtradata || ({} as any);
    const status = statusRaw ? statusRaw.toString() : remoteErrorCode;
    let { attempts } = remoteExtradata || ({} as any);
    attempts = attempts || this.getIncorrectAttempts(documentNumber);
    if (
      [
        UserLoginStatus.INACTIVE,
        UserLoginStatus.NOT_FOUND,
        UserLoginStatus.INVALID,
        UserLoginStatus.INVALID_LOGIN
      ].includes(status)
    ) {
      this.navToUserNotFound(documentNumber);
    } else if (status === UserLoginStatus.BLOCKED_ONE_DAY) {
      if (attempts === LoginHelper.MAX_LOGIN_ATTEMPTS) {
        const structureDevice = JSON.stringify(this.registerHelperService.getDeviceStructure(ForgetOperation.USER_NOT_FOUND));
        this.or.registerForgetPasswordOperation(
          ForgetOperation.ATTEMPTS_EXCEEDED_USER_LOGIN,
          ForgotPasswordOrigin.FROM_LOGIN,
          documentNumber,
          structureDevice
        );
      }
      this.nav.toNew(['login', 'blocked'], {
        state: {
          documentNumber,
          email
        },
        queryParams: {
          oneDayBlocked: true
        }
      });
    } else if (status === UserLoginStatus.FORGET_PROCCESS_PENDING) {
      if (attempts === LoginHelper.MAX_LOGIN_ATTEMPTS) {
        const structureDevice = JSON.stringify(this.registerHelperService.getDeviceStructure(ForgetOperation.USER_NOT_FOUND));
        this.or.registerForgetPasswordOperation(
          ForgetOperation.ATTEMPTS_EXCEEDED_USER_LOGIN,
          ForgotPasswordOrigin.FROM_LOGIN,
          documentNumber,
          structureDevice
        );
      }
      this.nav.toNew(['login', LoginRouting.FORGOT_PASSWORD, this.ctx?.isDesktop ? LoginRouting.MAIL : LoginRouting.PHONE], {
        queryParams: { documentNumber }
      });
    } else if (
      status === UserLoginStatus.ACTIVE_BUT_BLOCKED &&
      remoteHttpStatus === HttpStatusCode.FORBIDDEN
    ) {
      const state: ForgotPasswordState = {
        documentNumber,
        currentPassword: password
      } as ForgotPasswordState;
      this.nav.toNew(['login', LoginRouting.FORGOT_PASSWORD, LoginRouting.CREATE_PASSWORD], { state });
    } else if (
      status === UserLoginStatus.ACTIVE_BUT_BLOCKED &&
      remoteHttpStatus === HttpStatusCode.UNAUTHORIZED
    ) {
      if (notShowMatErrorsCallback) { notShowMatErrorsCallback(); }
      const localState = {documentNumber};
      const sendTemporalPassword = (): Observable<any> => this.forgotPasswordService.sendTemporalPassword(localState);
      this.forgotHelper.navToUserBlockedForSecurity(email, sendTemporalPassword);
    } else if (
      status === UserLoginStatus.OPERATIVE &&
      remoteHttpStatus === HttpStatusCode.UNAUTHORIZED
    ) {
      this.setIncorrectAttempts(documentNumber, attempts);
      if (showMatErrorsCallback) { showMatErrorsCallback(); }
      if (this.checkAttemptsExceeded(documentNumber)) {
        this.forgotHelper.navToLockUser(remoteExtradata?.email);
      }
    } else if (status === UserLoginStatus.CAMPAIGN_INACTIVE) {
      if (notShowMatErrorsCallback) { notShowMatErrorsCallback(); }

      const isRegisterEnabled = this.mmobileHelper.getCustomConfigData('customerSignUp', 'active');

      if (isRegisterEnabled) {
        this.registerHelperService.startRegisterOnlineCampaign({ documentNumber, password })
        return;
      }
      this.navToUserNotFound(documentNumber);
    }
    else if (
      [
        UserLoginStatus.CANCELLED,
        UserLoginStatus.IS_CANCELLED,
      ].includes(status)
    ) {
      this.nav.toNew(['login', 'user-cancelled'], {
        queryParams: { navFrom: 'login-helper' }
      });
    } else {
      this.showUserStatusGenericError(status);
    }
  }

  async onBusinessErrorLogin(
    error: BusinessLoginError,
    userData?: BusinessLoginUserData
  ) {
    const opts = {
      image: 'assets/images/result/error.svg',
      title: null,
      description: null,
      subDescription: null,
      okText: 'LOGIN.BUSINESS_ERROR.OK_BUTTON',
    };
    switch (error?.errorCode) {
      case BusinessLoginErrorCodes.UNAUTHORIZED:
        opts.title = 'LOGIN.BUSINESS_ERROR.UNAUTHORIZED.TITLE';
        opts.description = 'LOGIN.BUSINESS_ERROR.UNAUTHORIZED.DESCRIPTION';
        opts.subDescription = 'LOGIN.BUSINESS_ERROR.UNAUTHORIZED.SUB_DESCRIPTION';
        break;
      case BusinessLoginErrorCodes.USER_BLOCKED:
        opts.title = 'LOGIN.BUSINESS_ERROR.USER_BLOCKED.TITLE';
        opts.description = 'LOGIN.BUSINESS_ERROR.USER_BLOCKED.DESCRIPTION';
        break;
      case BusinessLoginErrorCodes.BLOCKING_USER:
        opts.title = 'LOGIN.BUSINESS_ERROR.BLOCKING_USER.TITLE';
        opts.description = 'LOGIN.BUSINESS_ERROR.BLOCKING_USER.DESCRIPTION';
        break;
      case BusinessLoginErrorCodes.USER_NOT_OPERATIVE:
        opts.title = 'LOGIN.BUSINESS_ERROR.NOT_OPERATIVE.TITLE';
        break;
      case BusinessLoginErrorCodes.UNVERIFIED_EMAIL:
        return this.ui.openComponentAsModal(
          ValidateEmailComponent,
          {
            userData: {
              cif: userData?.cif,
              nif: userData?.nif,
              email: error?.errorMessage?.email,
              signedToken: error?.errorMessage?.signedToken,
            }
          },
          'validate-email-modal'
        );
      case BusinessLoginErrorCodes.INTERNAL_ERROR:
      case BusinessLoginErrorCodes.BAD_REQUEST:
      default:
        opts.title = 'LOGIN.BUSINESS_ERROR.GENERIC_ERROR.TITLE';
        opts.description = 'LOGIN.BUSINESS_ERROR.GENERIC_ERROR.DESCRIPTION';
        break;
    }
    await this.ui.presentOneButtonAlert({
      image: opts.image,
      title: opts.title,
      description: opts.description,
      subdescription: opts.subDescription,
      okText: opts.okText,
      cssClass: !this.ctx.isApp ? ['business-login-alert'] : []
    });
  }

  navToUserNotFound(documentNumber) {
    const structureDevice = JSON.stringify(this.registerHelperService.getDeviceStructure(ForgetOperation.USER_NOT_FOUND));
    this.or.registerForgetPasswordOperation(
      ForgetOperation.USER_NOT_FOUND,
      ForgotPasswordOrigin.FROM_LOGIN,
      documentNumber,
      structureDevice
    );
    this.forgotHelper.analytics = {
      Pagetitle: this.getPageTitle(':step_error-usuario_no_existe'),
    };
    this.forgotHelper.navToUserNotFoundByLogin();
  }

  getPageTitle(title: string): string {
    return this.analyticsContext.getPageTitle(title);
  }

  private showUserStatusGenericError(status) {
    const notValidStatuses = new Set([8, 9, 10, 11, 12, 20]);
    if (notValidStatuses.has(status)) {
      this.ui.presentAlert(
        'El estado de tu cuenta no es válido. Por favor, ponte en contacto con nosotros para intentar solucionar el error',
        '',
        '',
        'Continuar',
        () => {
          this.nav.toNew('client-area/login-contact-form');
        }
      );
    } else {
      this.ui.presentAlert(
        'Se ha producido un error en el servidor. Por favor, inténtalo de nuevo más tarde',
        'INFO'
      );
    }
  }

  private setIncorrectAttempts(documentNumber: string, attempts: number) {
    this.incorrectLoginAttempts.set(documentNumber, attempts);
  }

  async afterLoginNavigation() {
    await this.ui.presentLoading('teCuidamos-state');
    const handledPendingDeeplink =  await this.deeplinkService.proccessPendingDeeplinkIfExists();
    this.ui.dismissLoading('teCuidamos-state');
    if (handledPendingDeeplink) {
      return;
    }

    this.guidedLoginParams = this.guidedLoginHelper.getStoredGuidedLoginParams();
    if (this.guidedLoginParams && this.auth.checkUserIsParticular()) {
      this.guidedLogin();
    } else {
      this.nav.goHomePage();
    }
  }

  private async guidedLogin() {
    switch (this.guidedLoginParams?.serviceId) {
      case GuidedLoginServiceId.FAMILIES:
      case GuidedLoginServiceId.FAMILIES_BULLETIN:
        this.proceedToFamiliesGuidedLogin();
        break;
      case GuidedLoginServiceId.MAPFRE_TECUIDAMOS:
        if (this.ctx.isApp) {
          break;
        }
        await this.proceedToTeCuidamosGuidedLogin();
        break;
      default:
        this.nav.goHomePage();
        break;
    }
    this.guidedLoginHelper.removeGuidedLoginParams();
  }

  proceedToFamiliesGuidedLogin() {
    this.familiesHelper.navToFamilies('forward', 'client-area/home', false);
  }

  private async proceedToTeCuidamosGuidedLogin() {
    await this.ui.presentLoading('teCuidamos-state');
    this.teCuidamos.getState$().subscribe(
      (state) => {
        if (Object.keys(state).length !== 0) {
          this.teCuidamos.navToTeCuidamos('forward', state.memberState, 'guided-login', this.guidedLoginParams?.params);
          this.ui.dismissLoading('teCuidamos-state');
        }
      },
      () => {
        this.nav.goHomePage();
        this.ui.dismissLoading('teCuidamos-state');
      }
    );
  }
}
